/**
 * The Table view: every change as a row (the map's stations, plus the before-hire steps),
 * oldest first. Each row says what happened in words, with a dot in each changed line's
 * colour; the Reason column is UKG's description of its reason code, with the code beside
 * it (a code the code tables don't describe shows alone: a description is never made up).
 * A row a finding names carries the map's finding marker.
 */
import { Tip } from './Tip'
import { LineDot, SevIcon } from './Marks'
import { changeSummary, changeText, findingHelp, findingsOn, fmtDate, rowLines } from './lifelineModel'

const NO_DESCRIPTION = 'UKG\'s code tables hold no description for this reason code.'

export function ReasonText({ reason, code }) {
  if (!reason && !code) return '—'
  return (
    <>
      {reason && <span>{reason}</span>}
      {code && (reason
        ? <span className="ll-mono ll-code">{code}</span>
        : <Tip text={NO_DESCRIPTION}><span className="ll-mono ll-code ll-code--bare" tabIndex={0}>{code}</span></Tip>)}
    </>
  )
}

// A fixed slot before the dots (only when some row is marked), so every row's words line up.
function FindingMarks({ list, onSelect, slot }) {
  if (!slot) return null
  return <span className="ll-marks">{list.map(m => (
    <Tip key={m.id} text={findingHelp(m)}>
      <button type="button" className="ll-row-finding" aria-label={`Finding: ${m.finding?.title || 'finding'}`}
              onClick={() => onSelect?.({ type: 'finding', id: m.id })}>
        <SevIcon tier={m.severity} size={13} />
      </button>
    </Tip>
  ))}</span>
}

export default function LifelineTable({ stationsList, preEvents, events = null, markers = [], onSelect = null }) {
  if (!stationsList.length && !preEvents.length) {
    return (
      <div className="ll-card">
        <h2>Every change</h2>
        <p className="ll-na"><b>No changes to list.</b> {events?.available === false ? events.reason : 'Nothing in this person’s job history has changed.'}</p>
      </div>
    )
  }
  const slot = [...preEvents, ...stationsList].some(r => findingsOn(markers, r.date).length > 0)
  return (
    <div className="ll-card">
      <h2>Every change</h2>
      <div className="ll-table-wrap">
        <table className="ll-table ll-changes">
          <colgroup><col className="ll-col-date" /><col className="ll-col-what" /><col className="ll-col-changes" /><col className="ll-col-reason" /></colgroup>
          <thead><tr>
            <th>Date</th>
            <th><Tip text="What happened: hired, a status change, or the lines that changed. A dot per line, in its colour on the map; a marker where a finding names the date."><span tabIndex={0}>What</span></Tip></th>
            <th><Tip text="Each line that changed, from → to, one per line. Several in one row changed together, in one transaction."><span tabIndex={0}>Changes</span></Tip></th>
            <th><Tip text="UKG's reason for the change, with its reason code; — when none was recorded."><span tabIndex={0}>Reason</span></Tip></th>
          </tr></thead>
          <tbody>
            {preEvents.map(e => (
              <tr key={e.id} className="ll-row-pre">
                <td className="ll-mono ll-date">{fmtDate(e.date)}</td>
                <td><span className="ll-what"><FindingMarks list={findingsOn(markers, e.date)} onSelect={onSelect} slot={slot} />
                  {e.line && <LineDot line={e.line} />}<span className="ll-pre-tag">Before hire</span> {e.title}</span></td>
                <td>{e.detail || '—'}</td>
                <td>—</td>
              </tr>
            ))}
            {stationsList.map(s => (
              <tr key={s.id}>
                <td className="ll-mono ll-date">{fmtDate(s.date)}</td>
                <td><span className="ll-what"><FindingMarks list={findingsOn(markers, s.date)} onSelect={onSelect} slot={slot} />
                  <span className="ll-dots">{rowLines(s).map(l => <LineDot key={l} line={l} />)}</span>{changeSummary(s)}</span></td>
                <td>{s.changes?.length
                  ? <ul className="ll-change-list">{s.changes.map((c, i) => <li key={i}><LineDot line={c.line} /><span>{changeText(c)}</span></li>)}</ul>
                  : '—'}</td>
                <td className="ll-reason"><ReasonText reason={s.reason} code={s.reason_code} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
